import { aria } from "../../js/path";
import moment from "moment";

// Aria's own status vocabulary, used by the Status field on the edit screen.
const statusOptions = [
  { value: "", text: "All Status" },
  { value: "generated", text: "Generated" },
  { value: "reviewed", text: "Reviewed" },
];

// The list endpoint is the only one available, so rows fetched for the table are
// kept here for the edit screen to read back without a detail endpoint.
const rowCache = {
  rows: [],
};

// Aria ignores every date parameter we tried (they behave exactly like an
// unknown param) and also ignores per_page, so a payment-date range has to be
// applied in the browser over rows collected page by page. This caps how many
// pages that sweep will request.
const DATE_FILTER_MAX_PAGES = 20;

export default {
  data() {
    return {
      filter: null,
      pageNo: null,
      tableData: [],
      sortBy: "payment_date",
      sortDesc: true,
      filterOn: [],
      currentPage: 1,
      perPage: 10,
      key: 0,
      statusFilter: "",
      dateRange: null,
      statusOptions: statusOptions,
      // id of the row currently being sent, so only its button spins
      sendingWhatsappId: null,
      // conversation modal
      conversationModal: false,
      conversationLoading: false,
      conversation: null,
      conversationFor: "",
      // one shared player for voice-note thumbnails, so only one clip plays
      audioPlayer: null,
      playingAudioUrl: null,
      // list refresh — prescriptions arrive while the page is open, so the
      // doctor needs the list to catch up without reloading the whole admin app
      autoRefresh: false,
      refreshSeconds: 30,
      refreshTimer: null,
      lastUpdated: null,
      // Prescription ids already on screen, so a background refresh can tell a
      // genuinely new arrival from the same list fetched again.
      seenIds: new Set(),
      newCount: 0,
      audio: null,
      // edit screen — the html document is the only thing edited here
      form: {
        id: null,
        html: "",
      },
      fields: [
        { key: "srno", label: "#", class: "text-center", thStyle: { width: "56px" } },
        { key: "patient_name", label: "Patient" },
        { key: "age", label: "Age" },
        { key: "sex", label: "Sex" },
        { key: "mobile", label: "Mobile", class: "c_touchpt_header" },
        { key: "doctor_name", label: "Doctor" },
        { key: "diagnosis", label: "Diagnosis", class: "w-250" },
        { key: "status", label: "Status" },
        { key: "payment_date", label: "Payment Date" },
        { key: "pdf_url", label: "PDF" },
        { key: "conversation", label: "Chat" },
        { key: "whatsapp", label: "WhatsApp" },
        { key: "edit", label: "Edit" },
      ],
    };
  },
  computed: {
    dateFilterActive() {
      return !!(this.dateRange && this.dateRange[0] && this.dateRange[1]);
    },
  },
  methods: {
    // ---------- list ----------
    // `quiet` is used by the background refresh: no blocking loader over a page
    // the doctor is reading, and no toast every 30s if the network drops.
    async fetchData(options) {
      const quiet = options === true || (options && options.quiet === true);

      // A date range cannot be pushed down to Aria, so those rows are gathered
      // page by page and filtered here instead.
      if (this.dateFilterActive) return this.fetchForDateFilter();

      // AriaPrescriptionService forwards page/search straight through and maps
      // `filter` onto Aria's `status`, omitting it when it is "all".
      let url = aria.ariaPrescriptionsUrl + `?page=${this.currentPage}`;
      if (this.filter) url += `&search=${encodeURIComponent(this.filter)}`;
      if (this.statusFilter) url += `&filter=${this.statusFilter}`;

      this.getUrl();
      if (!quiet) this.$store.commit("loader/updateStatus", true);
      try {
        const res = await this.getRequest(url);
        if (res.status) {
          const rows = res.response?.data ?? [];
          const meta = res.response?.pagination ?? {};
          rowCache.rows = rows;
          this.tableData = {
            data: rows,
            total: meta.total ?? rows.length,
            // Aria ignores per_page and always serves its own page size.
            per_page: meta.per_page ?? this.perPage,
            total_pages: meta.total_pages ?? 1,
            current_page: meta.page ?? this.currentPage,
          };
          this.perPage = meta.per_page ?? this.perPage;
          this.lastUpdated = moment().format("HH:mm:ss");
          // A background refresh announces arrivals; a deliberate one — a page
          // change, a filter, the Refresh button — just re-baselines quietly,
          // since its rows are new to the screen but not new to the clinic.
          if (quiet) this.announceNewRows(rows);
          else this.baselineRows(rows);
        } else if (!quiet) {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: res.message || "Could not load prescriptions",
          });
        }
      } catch (err) {
        if (!quiet) {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: err?.data?.message || "Something went wrong",
          });
        }
      }
      this.key += 1;
      if (!quiet) this.$store.commit("loader/updateStatus", false);
    },

    // Counts down, so the newest prescription at the top carries the highest
    // number and #1 is the oldest. Numbering runs on across pages — with 45
    // prescriptions, page 2 continues at 25 rather than restarting.
    serialNumber(index) {
      const page = this.currentPage || 1;
      const size = this.perPage || 10;
      const position = (page - 1) * size + index;
      const total = (this.tableData && this.tableData.total) || 0;
      // Without a total there is nothing to count down from, so number upward
      // rather than showing a negative.
      return total ? total - position : position + 1;
    },

    // ---------- keeping the list current ----------
    refreshList() {
      this.fetchData();
    },
    // Skips a beat rather than fighting the doctor for the page: nothing to see
    // on a hidden tab, no reload underneath an open conversation, and no
    // twenty-request date sweep every interval.
    tickRefresh() {
      if (document.hidden) return;
      if (this.conversationModal) return;
      if (this.dateFilterActive) return;
      this.fetchData({ quiet: true });
    },
    // ---------- announcing arrivals ----------
    idsOf(rows) {
      return (rows || []).map((row) => row.id).filter((id) => id != null);
    },
    baselineRows(rows) {
      this.seenIds = new Set(this.idsOf(rows));
      this.newCount = 0;
    },
    announceNewRows(rows) {
      const ids = this.idsOf(rows);
      // Nothing to compare against yet: adopt the list rather than announcing
      // every prescription on it.
      if (!this.seenIds.size) {
        this.baselineRows(rows);
        return;
      }
      const fresh = ids.filter((id) => !this.seenIds.has(id));
      ids.forEach((id) => this.seenIds.add(id));
      if (!fresh.length) return;
      this.newCount = fresh.length;
      this.playChime();
    },
    // Synthesised rather than shipped as an audio file: two short notes, no
    // asset to load and nothing to go missing behind a CDN.
    playChime() {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      try {
        if (!this.audio) this.audio = new Ctx();
        if (this.audio.state === "suspended") this.audio.resume().catch(() => {});
        const start = this.audio.currentTime;
        [
          [880, 0],
          [1174.7, 0.16],
        ].forEach(([hz, at]) => {
          const osc = this.audio.createOscillator();
          const gain = this.audio.createGain();
          osc.type = "sine";
          osc.frequency.value = hz;
          // Ramped rather than switched on, so it reads as a chime not a click.
          gain.gain.setValueAtTime(0.0001, start + at);
          gain.gain.exponentialRampToValueAtTime(0.12, start + at + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + at + 0.22);
          osc.connect(gain).connect(this.audio.destination);
          osc.start(start + at);
          osc.stop(start + at + 0.25);
        });
      } catch (err) {
        // A browser that refuses to make noise must not break the refresh.
      }
    },
    // Browsers only allow audio that a user asked for, so the context is opened
    // on the click that switches Auto on — the gesture that grants permission.
    primeAudio() {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      try {
        if (!this.audio) this.audio = new Ctx();
        if (this.audio.state === "suspended") this.audio.resume().catch(() => {});
      } catch (err) {
        // no audio available; the list still refreshes
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.primeAudio();
      this.refreshTimer = setInterval(
        this.tickRefresh,
        this.refreshSeconds * 1000
      );
      // Coming back to the tab is the moment the list is most likely stale.
      document.addEventListener("visibilitychange", this.onVisible);
    },
    stopAutoRefresh() {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      document.removeEventListener("visibilitychange", this.onVisible);
    },
    onVisible() {
      if (!document.hidden) this.tickRefresh();
    },
    // Walks Aria's pages, keeps the rows whose payment_date falls in range, and
    // pages the result locally so the pager reflects the filtered count.
    async fetchForDateFilter() {
      const [from, to] = this.dateRange;
      const extra =
        (this.filter ? `&search=${encodeURIComponent(this.filter)}` : "") +
        (this.statusFilter ? `&filter=${this.statusFilter}` : "");

      this.getUrl();
      this.$store.commit("loader/updateStatus", true);

      const collected = [];
      let truncated = false;
      let reportedTotal = 0;
      try {
        for (let page = 1; page <= DATE_FILTER_MAX_PAGES; page++) {
          const res = await this.getRequest(
            `${aria.ariaPrescriptionsUrl}?page=${page}${extra}`
          );
          if (!res.status) break;
          const rows = res.response?.data ?? [];
          const meta = res.response?.pagination ?? {};
          collected.push(...rows);
          reportedTotal = meta.total ?? collected.length;
          const lastPage = meta.total_pages ?? 1;
          if (!rows.length || page >= lastPage) break;
          if (page === DATE_FILTER_MAX_PAGES && page < lastPage) truncated = true;
        }

        rowCache.rows = collected;
        const matched = collected.filter((row) => {
          if (!row.payment_date) return false;
          const day = moment(row.payment_date).format("YYYY-MM-DD");
          return day >= from && day <= to;
        });

        const total = matched.length;
        const totalPages = Math.ceil(total / this.perPage) || 1;
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        const start = (this.currentPage - 1) * this.perPage;

        this.tableData = {
          data: matched.slice(start, start + this.perPage),
          total: total,
          per_page: this.perPage,
          total_pages: totalPages,
          current_page: this.currentPage,
        };

        // Say so rather than quietly filtering a truncated set.
        if (truncated) {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "warning",
            title: `Date filter covered the first ${collected.length} of ${reportedTotal} prescriptions only`,
          });
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err?.data?.message || "Something went wrong",
        });
      }
      this.key += 1;
      this.$store.commit("loader/updateStatus", false);
    },
    searchFor() {
      this.currentPage = 1;
      this.fetchData();
    },
    search(event) {
      if (this.filter.length > 1) {
        if (event.keyCode == 13) {
          this.currentPage = 1;
          this.fetchData();
        }
      } else if (this.filter.length == 0) {
        this.currentPage = 1;
        this.fetchData();
      }
    },
    applyFilter() {
      this.currentPage = 1;
      this.fetchData();
    },
    clearFilter() {
      this.filter = null;
      this.statusFilter = "";
      this.dateRange = null;
      this.currentPage = 1;
      this.fetchData();
    },
    filterPage() {
      if (this.pageNo.length > 0) {
        this.currentPage = parseInt(this.pageNo);
        this.fetchData();
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    // Several stored pdf_urls point at localhost or placeholder buckets, which
    // would reach the patient as a dead link. Only pass one through when it
    // looks publicly fetchable; otherwise send the html and let the backend
    // render a fresh pdf.
    isPublicPdfUrl(url) {
      if (!url) return false;
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== "https:") return false;
        return !/^(localhost|127\.0\.0\.1)$/.test(parsed.hostname);
      } catch (err) {
        return false;
      }
    },
    // Sends the prescription to the patient over WhatsApp. Outward facing and
    // not undoable, so it always confirms first.
    async sendWhatsapp(row) {
      if (!row.mobile) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "This prescription has no mobile number to send to",
        });
        return;
      }
      const publicPdf = this.isPublicPdfUrl(row.pdf_url);
      if (!publicPdf && !row.html) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "Nothing to send — the prescription has no HTML or usable PDF",
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Send this prescription to ${row.patient_name || "the patient"} on ${
          row.mobile
        }? The patient receives it immediately.`,
        {
          title: "Send on WhatsApp",
          okTitle: "Send",
          okVariant: "success",
          cancelTitle: "Cancel",
          centered: true,
        }
      );
      if (!confirmed) return;

      const payload = { mobile_number: row.mobile };
      if (row.patient_name) payload.patient_name = row.patient_name;
      if (publicPdf) payload.pdf_url = row.pdf_url;
      // Always include the html when present; the backend prefers pdf_url and
      // falls back to rendering this.
      if (row.html) payload.html = row.html;

      this.sendingWhatsappId = row.id;
      try {
        const res = await this.postRequest(
          `${aria.ariaSendWhatsappUrl}/${row.id}`,
          payload
        );
        if (res && res.status) {
          // When the backend generated the pdf it returns the new url.
          if (res.pdf_url) {
            row.pdf_url = res.pdf_url;
            const cached = rowCache.rows.find((e) => e.id == row.id);
            if (cached) cached.pdf_url = res.pdf_url;
          }
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "success",
            title: res.message || `Prescription sent to ${row.mobile}`,
          });
        } else if (res) {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: res.message || "The prescription could not be sent",
          });
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err?.data?.message || "Something went wrong",
        });
      }
      this.sendingWhatsappId = null;
      this.key += 1;
    },
    // The conversation endpoint is keyed on consultation_id, not the
    // prescription id — passing the latter returns 404.
    async openConversation(row) {
      if (!row.consultation_id) return;
      this.conversationFor = row.patient_name || "";
      this.conversation = null;
      this.conversationModal = true;
      this.conversationLoading = true;
      try {
        const res = await this.getRequest(
          `${aria.ariaConversationUrl}/${row.consultation_id}`
        );
        if (res.status) {
          this.conversation = res.response?.conversation ?? null;
        } else {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: res.message || "Could not load the conversation",
          });
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err?.data?.message || "Something went wrong",
        });
      }
      this.conversationLoading = false;
    },
    // Epoch milliseconds on every message and media item.
    formatChatTime(ms) {
      if (!ms) return "";
      return moment(ms).format("DD MMM YYYY, hh:mm A");
    },
    // Inserts a date separator whenever the day changes down the thread.
    chatDaySeparator(messages, index) {
      const current = messages[index];
      if (!current || !current.createdAt) return null;
      const day = moment(current.createdAt).format("DD MMM YYYY");
      if (index === 0) return day;
      const previous = messages[index - 1];
      const prevDay = previous?.createdAt
        ? moment(previous.createdAt).format("DD MMM YYYY")
        : null;
      return day === prevDay ? null : day;
    },
    // The English rendering of a message, when there is one worth showing.
    // Aria fills textEn even for messages already in English, so an identical
    // line is suppressed rather than printed twice under itself.
    translationOf(message) {
      if (!message) return "";
      const english = (message.textEn || "").trim();
      if (!english) return "";
      const original = (message.text || "").trim();
      return english.toLowerCase() === original.toLowerCase() ? "" : english;
    },
    // Attachments can be images or PDFs; a PDF in an <img> renders broken, so
    // check the mime type when Aria sends one, else the url's extension.
    isPdfMedia(url, item = {}) {
      const mime = (
        item.mimeType || item.mime_type || item.contentType || item.mime || ""
      ).toLowerCase();
      if (mime) return mime.includes("pdf");
      if (!url) return false;
      const path = url.split(/[?#]/)[0].toLowerCase();
      return path.endsWith(".pdf") || url.startsWith("data:application/pdf");
    },
    // Message mediaUrl can point at a CDN host that 403s while conversation.media
    // carries a working URL for the same file, so prefer the media entry whose
    // file name matches.
    messageMediaUrl(m) {
      const url = m.mediaUrl;
      if (!url) return url;
      const fileName = (u) => (u || "").split(/[?#]/)[0].split("/").pop();
      const name = fileName(url);
      const match = (this.conversation?.media || []).find(
        (md) => md.url && fileName(md.url) === name
      );
      return match ? match.url : url;
    },
    isAudioMedia(url, item = {}) {
      const mime = (
        item.mimeType || item.mime_type || item.contentType || item.mime || ""
      ).toLowerCase();
      if (mime) return mime.startsWith("audio/");
      const kind = (item.mediaKind || item.kind || "").toLowerCase();
      if (/audio|voice/.test(kind)) return true;
      if (!url) return false;
      if (url.startsWith("data:audio/")) return true;
      const path = url.split(/[?#]/)[0].toLowerCase();
      return /\.(mp3|ogg|oga|opus|wav|m4a|aac|amr|weba|webm|3gp)$/.test(path);
    },
    toggleAudio(url) {
      if (this.playingAudioUrl === url) {
        this.stopAudio();
        return;
      }
      this.stopAudio();
      const player = new Audio(url);
      player.onended = () => this.stopAudio();
      player.onerror = () => {
        this.stopAudio();
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "Could not play this audio",
        });
      };
      this.audioPlayer = player;
      this.playingAudioUrl = url;
      player.play().catch(() => this.stopAudio());
    },
    stopAudio() {
      if (this.audioPlayer) {
        this.audioPlayer.onended = null;
        this.audioPlayer.onerror = null;
        this.audioPlayer.pause();
      }
      this.audioPlayer = null;
      this.playingAudioUrl = null;
    },
    mediaKindLabel(kind) {
      if (!kind) return "Attachment";
      return kind.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    statusTextClass(status) {
      if (status == "reviewed") return "text-success font-weight-bold";
      if (status == "generated") return "text-warning font-weight-bold";
      return "text-muted";
    },
    // Inline status change from the table, through the same update endpoint the
    // edit screen uses. The row is only mutated once the API confirms it.
    async updateRowStatus(row, next) {
      if (!next || next === row.status) return;
      const previous = row.status;

      this.$store.commit("loader/updateStatus", true);
      try {
        const res = await this.putRequest(
          `${aria.ariaPrescriptionUpdateUrl}/${row.id}`,
          { status: next }
        );
        // Aria answers 200 with status:true even for fields it ignores, so take
        // the status from the record it echoes back rather than from `next`.
        const applied = res?.response?.data?.status;
        if (res.status && applied === next) {
          row.status = applied;
          const cached = rowCache.rows.find((e) => e.id == row.id);
          if (cached) cached.status = applied;
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "success",
            title: `Status changed to ${applied}`,
          });
        } else {
          row.status = previous;
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: res?.message || "Status was not changed",
          });
        }
      } catch (err) {
        row.status = previous;
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err?.data?.message || "Something went wrong",
        });
      }
      this.key += 1;
      this.$store.commit("loader/updateStatus", false);
    },
    formatDateTime(value) {
      if (!value) return "---";
      return moment(value).format("DD MMM YYYY, hh:mm A");
    },

    // ---------- edit ----------
    async fetchPrescription(id) {
      let row = rowCache.rows.find((e) => e.id == id);
      // Deep link or hard refresh: the cache is empty, so pull a page and look
      // the record up in it. Replace with a detail endpoint once one exists.
      if (!row) {
        this.$store.commit("loader/updateStatus", true);
        try {
          const res = await this.getRequest(
            aria.ariaPrescriptionsUrl + "?page=1&per_page=100"
          );
          if (res.status) {
            rowCache.rows = res.response?.data ?? [];
            row = rowCache.rows.find((e) => e.id == id);
          }
        } catch (err) {
          // fall through to the not-found branch below
        }
        this.$store.commit("loader/updateStatus", false);
      }
      if (!row) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "Prescription not found",
        });
        this.$router.push({ name: "aria" });
        return;
      }
      this.form = {
        id: row.id,
        html: row.html || "",
      };
    },
    // Aria's PUT takes a partial body — "send whatever you changed" — and the
    // html document is the only thing this screen edits.
    buildPayload() {
      const before = rowCache.rows.find((e) => e.id == this.form.id) || {};
      const payload = {};
      if (this.form.html !== (before.html || "")) payload.html = this.form.html;
      return payload;
    },
    async submitData() {
      const payload = this.buildPayload();
      if (!Object.keys(payload).length) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "info",
          title: "Nothing to update",
        });
        return;
      }

      this.$store.commit("loader/updateStatus", true);
      try {
        const res = await this.putRequest(
          `${aria.ariaPrescriptionUpdateUrl}/${this.form.id}`,
          payload
        );
        if (res.status) {
          // Keep the cached row in step so returning to the list shows the edit.
          const cached = rowCache.rows.find((e) => e.id == this.form.id);
          if (cached && payload.html !== undefined) cached.html = payload.html;
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "success",
            title: res.message || "Prescription updated successfully",
          });
          this.$router.push({ name: "aria" });
        } else {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: res.message || "Could not update the prescription",
          });
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err?.data?.message || "Something went wrong",
        });
      }
      this.$store.commit("loader/updateStatus", false);
    },

    // Prints the preview iframe in place — it already holds exactly this
    // document, so no second window is needed and the admin chrome stays out
    // of the printout.
    printPrescription() {
      if (!this.form.html) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "This prescription has no HTML to print",
        });
        return;
      }
      const preview = this.$refs.preview;
      if (!preview || !preview.print()) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "The preview is not ready yet, try again in a moment",
        });
      }
    },

    openPreviewInNewTab() {
      if (!this.form.html) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "This prescription has no HTML to preview",
        });
        return;
      }
      const win = window.open();
      if (!win) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: "Allow pop-ups for this site to open the preview",
        });
        return;
      }
      // Already a complete document, so write it as-is rather than nesting it
      // inside another <html><body>.
      win.document.open();
      win.document.write(this.form.html);
      win.document.close();
    },

    getUrl() {
      var url = new URL(window.location);
      const set = (key, value) => {
        if (value != null && value !== "") {
          url.searchParams.has(key)
            ? url.searchParams.set(key, value)
            : url.searchParams.append(key, value);
        } else {
          url.searchParams.delete(key);
        }
      };
      set("search", this.filter);
      set("filter", this.statusFilter);
      set("from_date", this.dateFilterActive ? this.dateRange[0] : null);
      set("to_date", this.dateFilterActive ? this.dateRange[1] : null);
      set("page", this.currentPage);
      url.search = url.searchParams;
      history.pushState({}, null, url.toString());
    },
  },
  watch: {
    // closing the chat shouldn't leave a voice note playing
    conversationModal(open) {
      if (!open) this.stopAudio();
    },
    currentPage: {
      handler: function (value) {
        if (typeof value != "undefined") {
          this.pageNo = this.currentPage;
          this.fetchData();
        }
      },
    },
    autoRefresh: function (on) {
      if (on) this.startAutoRefresh();
      else this.stopAutoRefresh();
    },
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    this.stopAudio();
    if (this.audio && this.audio.close) this.audio.close();
    this.audio = null;
  },
  created() {
    if (this.$route.name == "aria-edit-prescription") {
      this.fetchPrescription(this.$route.params.id);
      return;
    }
    if (this.$route.query.search) {
      this.filter = this.$route.query.search;
    }
    if (this.$route.query.filter) {
      this.statusFilter = this.$route.query.filter;
    }
    if (this.$route.query.from_date && this.$route.query.to_date) {
      this.dateRange = [this.$route.query.from_date, this.$route.query.to_date];
    }
    if (this.$route.query.page && this.$route.query.page != 1) {
      this.currentPage = parseInt(this.$route.query.page);
    } else {
      this.fetchData();
    }
  },
};
